import React, { useState } from "react";
import Label from "../../../components/FormComponents/Label";
import Toggle from "../../../components/FormComponents/Toggle";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import Input from "../../../components/FormComponents/Input";
import Textarea from "../../../components/FormComponents/Textarea";
import CustomJS from "../../../components/BuilderComponents/CustomJS";
import { useModalContext } from "../../../context/ModalContext";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import Button from "../../../components/FormComponents/Button";
import { toastError, toastSuccess } from "../../../utils/toastUtils";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";

const WebhookAPIKeyGenerator = ({ id, apiKey = "" }) => {
  const { getUserConfirmation } = useModalContext();
  const [webhookAPIKey, setWebhookAPIKey] = useState(apiKey);

  const generateAPIKey = async () => {
    try {
      let userConfirmed = true;
      if (apiKey) {
        userConfirmed = await getUserConfirmation("you want to generate <Label>API key</Label> ?");
      }
      if (userConfirmed) {
        const { data } = await axiosInstance.get(`/webhook/api-key/${id}`);
        setWebhookAPIKey(data.api_key);
      }
    } catch (error) {
      handleApiError(error);
    }
  };
  return (
    <div className="d_flex gap_3 ">
      <Input type="text" value={webhookAPIKey} disabled />
      <Button onClick={generateAPIKey}>Generate Key</Button>
    </div>
  );
};

const StringArraySelector = ({ stringArray, handleChange }) => {
  const [inputText, setInputText] = useState("");
  const handleStringInArray = () => {
    if (inputText.trim().length === 0) {
      toastError("Please enter a data");
      return;
    }
    handleChange("add", inputText);
    setInputText("");
  };
  const arrayListInChip = stringArray.map((string, index) => {
    return (
      <div className="selected_chip_items" key={string}>
        {string}
        <MaterialIcon name="close" onClick={() => handleChange("remove", index)} />
      </div>
    );
  });

  return (
    <>
      <div className="inline_tag_items add_apiVariable">
        <Input type="text" label="Enter Variables" value={inputText} onChange={(e) => setInputText(e.target.value)} />
        <Button variant="secondary" onClick={handleStringInArray}>
          Add +
        </Button>
      </div>
      {arrayListInChip.length > 0 && <div className="selected_chip_container m_0 mt_4">{arrayListInChip}</div>}
    </>
  );
};

const WebhookApiEditor = ({ file, selectedApiData, setSelectedApiData }) => {
  const [isSaving, setIsSaving] = useState(false);
  const { isDirty, markClean } = useDirtyChecker(selectedApiData);

  const apiOptions = [
    { id: "get", name: "GET" },
    { id: "post", name: "POST" },
    { id: "put", name: "PUT" },
    { id: "patch", name: "PATCH" },
    { id: "delete", name: "DELETE" }
  ];

  const handleInputChange = (name, value) => {
    const copyObject = { ...selectedApiData };
    copyObject[name] = value;
    setSelectedApiData(copyObject);
  };

  const handleChangeInStringArray = (type, method, data) => {
    const copyObject = { ...selectedApiData };
    if (method === "add") {
      copyObject[type] = [...copyObject[type], data];
    }
    if (method === "remove") {
      copyObject[type] = copyObject[type].filter((_, i) => i !== data);
    }
    setSelectedApiData(copyObject);
  };

  const handleCustomJSChange = (data) => {
    const copyApiData = JSON.parse(JSON.stringify(selectedApiData));
    copyApiData.custom_js = data;
    setSelectedApiData(copyApiData);
  };

  const getParseData = (config) => {
    try {
      config.projections = JSON.parse(config.projections);
      config.body = JSON.parse(config.body);
      return config;
    } catch (error) {
      handleApiError("Please Enter Valid JSON Data");
      return null;
    }
  };

  const handleSaveWebhook = async () => {
    try {
      setIsSaving(true);
      const payload = {
        ...selectedApiData,
        name: file.name
      };
      const newConfig = getParseData(JSON.parse(JSON.stringify(payload)));
      const response = await axiosInstance.patch(`/webhook/${file.id}`, newConfig);
      toastSuccess(response?.message || "Webhook Saved Successfully");
      markClean();
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <GenericActionBar
        path={
          <div>
            <em>API</em> → <span>{file.name}</span>
          </div>
        }
        actions={[
          {
            key: "save",
            icon: "save",
            title: isSaving ? "Saving..." : "Save",
            onClick: isSaving ? undefined : handleSaveWebhook
          }
        ]}
        options={{
          save: {
            className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
          }
        }}
      />
      <div className="page_main_section pb_5">
        <div className="w_75 m_auto">
          <div className="builder_form">
            <WebhookAPIKeyGenerator id={file.id} apiKey={selectedApiData?.api_key} />
            <div className="d_flex gap_2">
              <Label>Zino Auth</Label>
              <Toggle
                value={selectedApiData?.app_auth ?? false}
                onChange={(e) => handleInputChange("app_auth", e.target.checked)}
              />
            </div>
            <div className="inline_form_input">
              <SingleSelectBox
                selectedData={selectedApiData?.method}
                options={apiOptions}
                onChange={(obj) => handleInputChange("method", obj.id)}
                placeholder="Select Method"
                label="Select Method"
              />

              <div className="builder_form w_50">
                <Input
                  type="text"
                  label="Enter Path"
                  name="path"
                  value={selectedApiData?.path}
                  onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                />
              </div>
            </div>
            <div className="builder_form">
              <div className="api_variable_outer">
                <StringArraySelector
                  stringArray={selectedApiData?.variables}
                  handleChange={(method, data) => handleChangeInStringArray("variables", method, data)}
                />
              </div>
            </div>
            <div className="builder_form">
              <Textarea
                label="Enter Post body"
                value={selectedApiData?.body}
                name="body"
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
              />
            </div>

            <div className="">
              <CustomJS
                inputString={selectedApiData?.custom_js}
                handleChange={handleCustomJSChange}
                tableID={selectedApiData?.sm_id || selectedApiData?.rdbms_id || ""}
                table_type={selectedApiData?.table_type}
                showFullScreenIcon={false}
              />
            </div>
            <div className="builder_form">
              <Textarea
                label="Enter Projections"
                value={selectedApiData?.projections}
                name="projections"
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookApiEditor;
